import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Toast from '../components/Toast';
import BeforeAfterSlider from '../components/studio/BeforeAfterSlider';
import apiClient, { API_BASE_URL, analyzeImageWithAI, batchProcessImages, downloadFile, downloadWithMetadata, getImageMetadata, getSupportedImageFormats, processImagePipeline, uploadFile } from '../services/api';
import { buildBatchProcessRequest, getBatchQueueKey, prependSelectedBatchFile } from './image-studio/batchWorkflow';
import { formatBytes, formatDimension, truncateTwoWords } from './image-studio/formatters';
import StudioCanvas from './image-studio/StudioCanvas';
import ImageFileInfoCard from './image-studio/ImageFileInfoCard';
import BatchQueueCard from './image-studio/BatchQueueCard';
import ExportSettingsCard from './image-studio/ExportSettingsCard';
import ImageResultCard from './image-studio/ImageResultCard';
import ImagePipelinePanel from './image-studio/ImagePipelinePanel';
import { buildImageOperations } from './image-studio/buildOperations';
import { useImageStudio } from '../hooks/useImageStudio';
import './ImagePage.css';
import OperationCard from '../components/image-studio/OperationCard';
import StudioTopNav from '../components/image-studio/StudioTopNav';

const ImagePage = ({ theme, setTheme }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [renderedPreviewUrl, setRenderedPreviewUrl] = useState('');
   const [imageSize, setImageSize] = useState({ width: null, height: null });
   const [metadata, setMetadata] = useState(null);
   const [aiMetadata, setAiMetadata] = useState(null);
   const [includeAiMetadata, setIncludeAiMetadata] = useState(false);
   const [isAnalyzing, setIsAnalyzing] = useState(false);

   // Local UI state (not in reducer)
   const [cropAspectRatio, setCropAspectRatio] = useState(null);
   const [fastPreviewUrl, setFastPreviewUrl] = useState("");
   const [zoom, setZoom] = useState(1);
   const [pan, setPan] = useState({ x: 0, y: 0 });

   // Consolidated state management (pipeline + UI state + undo/redo)
   const studio = useImageStudio(imageSize);
   const { pipeline } = studio;
   const initializePipeline = studio.initializePipeline;

   const enabledOperations = useMemo(() => pipeline.filter((operation) => operation.enabled), [pipeline]);

   const resizeOperation = useMemo(() => pipeline.find((operation) => operation.type === 'resize'), [pipeline]);

   const resizeOverlayStyle = useMemo(() => {
     if (!resizeOperation?.enabled || !imageSize.width || !imageSize.height) return null;

     const targetWidth = Number(resizeOperation.width || imageSize.width);
     const targetHeight = Number(resizeOperation.height || imageSize.height);
     if (!targetWidth || !targetHeight) return null;

     const targetRatio = targetWidth / targetHeight;
     const sourceRatio = imageSize.width / imageSize.height;

     let style;
     if (targetRatio >= sourceRatio) {
       style = {
         width: '100%',
         height: `${(sourceRatio / targetRatio) * 100}%`,
         aspectRatio: `${targetWidth} / ${targetHeight}`,
         left: '50%',
         top: '50%',
       };
     } else {
       style = {
         width: `${(targetRatio / sourceRatio) * 100}%`,
         height: '100%',
         aspectRatio: `${targetWidth} / ${targetHeight}`,
         left: '50%',
         top: '50%',
       };
     }

     const wPercent = parseFloat(style.width);
     const hPercent = parseFloat(style.height);
     const left = (100 - wPercent) / 2;
     const top = (100 - hPercent) / 2;
     const right = left + wPercent;
     const bottom = top + hPercent;

     style.clipPathHole = `polygon(
       0% 0%, 0% 100%, 100% 100%, 100% 0%, 0% 0%,
       ${left}% ${top}%, ${right}% ${top}%, ${right}% ${bottom}%, ${left}% ${bottom}%, ${left}% ${top}%
     )`;

     return style;
   }, [imageSize.width, imageSize.height, resizeOperation]);

   const buildOperations = useCallback(() => buildImageOperations({ enabledOperations }), [enabledOperations]);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl((currentPreviewUrl) => {
        if (currentPreviewUrl) URL.revokeObjectURL(currentPreviewUrl);
        return '';
      });
      setImageSize({ width: null, height: null });
      setMetadata(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);

    // Immediately populate metadata from the browser File object
    const ext = selectedFile.name.split('.').pop()?.toLowerCase() || '';
    const mimeType = selectedFile.type || `image/${ext}`;
    setMetadata({
      name: selectedFile.name,
      size: selectedFile.size,
      type: mimeType,
      ext: ext.toUpperCase(),
      colorProfile: 'sRGB',
      lastModified: new Date(selectedFile.lastModified).toLocaleString(),
    });

    const img = new Image();
    img.onload = () => {
      const size = { width: img.width, height: img.height };
      setImageSize(size);
      if (!initialDimensionsSyncedRef.current) {
        initializePipeline(size);
        initialDimensionsSyncedRef.current = true;
      }
    };
    img.src = objectUrl;

    return () => URL.revokeObjectURL(objectUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, initializePipeline]);




  const [activeOperation, setActiveOperation] = useState(null);

  // Deep-link: /image?tool=crop → pre-expand that panel
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const tool = searchParams.get('tool');
    const validTools = ['resize', 'crop', 'effect', 'watermark', 'brightness'];
    if (tool && validTools.includes(tool)) {
      setActiveOperation(tool);
      setSearchParams({}, { replace: true }); // clean URL
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [dockTool, setDockTool] = useState(null);
  const [conversionResult, setConversionResult] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [outputFormat, setOutputFormat] = useState('jpg');
  const [supportedOutputFormats, setSupportedOutputFormats] = useState(['jpg', 'png', 'webp', 'tiff', 'avif', 'bmp', 'gif', 'heic', 'ico']);
   const [quality, setQuality] = useState(100);
   const [removeExif, setRemoveExif] = useState(false);

   useEffect(() => {
     const fetchFormats = async () => {
       try {
         const formats = await getSupportedImageFormats();
         if (formats?.output) {
           setSupportedOutputFormats(formats.output.map(f => f.value));
         }
       } catch (err) {
         console.error("Failed to fetch supported formats", err);
       }
     };
     fetchFormats();
   }, []);


   // Ref state (not part of reducer)
   const fileInputRef = useRef(null);
   const uploadTokenRef = useRef(0);
   const initialDimensionsSyncedRef = useRef(false);

    // Dragging state for resize handles
    const [isResizing, setIsResizing] = useState(false);
    const [resizeHandle, setResizeHandle] = useState(null);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0, width: 0, height: 0 });

    // Derived operations

    const showToast = useCallback((message, type = 'info') => {
      studio.setToast({ message, type, id: Date.now() });
    }, [studio]);

    const addFilesToStudio = (incomingFiles) => {
      const files = Array.from(incomingFiles || []).filter((file) => {
        if (!file.type.startsWith('image/')) {
          studio.setToast(`Skipped ${file.name || 'file'} because it is not an image.`, 'error');
          return false;
        }
        return true;
      });

      if (files.length === 0) return;

      if (isBatchMode) {
        const merged = [...studio.batchQueue];
        const seen = new Set(merged.map(getBatchQueueKey));
        let added = 0;
        for (const file of files) {
          const key = getBatchQueueKey(file);
          if (!seen.has(key)) {
            merged.push(file);
            seen.add(key);
            studio.setBatchItemState(key, {
              status: 'queued',
              progress: 0,
              label: 'Queued',
            });
            added++;
          }
        }
        studio.setBatchQueue(merged);
        setConversionResult(null);
        setBatchResult(null);
        setRenderedPreviewUrl('');
        setDockTool('canvas');
        setActiveOperation('resize');
        initialDimensionsSyncedRef.current = false;
        studio.setToast(`${added} image(s) added to the batch queue.`, 'success');
        return;
      }

      const file = files[0];
      setSelectedFile(file);
      setConversionResult(null);
      setBatchResult(null);
      setRenderedPreviewUrl('');
      setDockTool('canvas');
      setActiveOperation('resize');
      initialDimensionsSyncedRef.current = false;
      studio.setToast('Image loaded into the studio.', 'success');
      
      // Trigger AI Analysis
      handleAiAnalysis(file);
    };

    const prependSelectedFileToBatchQueue = () => {
      if (!selectedFile) return;
      const selectedKey = getBatchQueueKey(selectedFile);
      studio.setBatchQueue(prependSelectedBatchFile(selectedFile, studio.batchQueue));
      studio.setBatchItemState(selectedKey, {
        status: 'queued',
        progress: 0,
        label: 'Queued',
      });
    };

    const handleInputChange = (event) => {
      addFilesToStudio(event.target.files);
      event.target.value = '';
    };

    const handleDrop = (event) => {
      event.preventDefault();
      studio.setIsDragOver(false);
      addFilesToStudio(event.dataTransfer.files);
    };

    const handleThemeToggle = () => {
      setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
    };

    const handleResizeStart = (e, handle) => {
      e.preventDefault();
      e.stopPropagation();
      const resizeOp = pipeline.find(op => op.type === 'resize');
      const overlayElement = e.target.closest('.resize-overlay');
      const rect = overlayElement ? overlayElement.getBoundingClientRect() : null;

      setIsResizing(true);
      setResizeHandle(handle);
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        width: Number(resizeOp?.width || imageSize.width),
        height: Number(resizeOp?.height || imageSize.height),
        displayWidth: rect ? rect.width : 1,
        displayHeight: rect ? rect.height : 1,
      });
    };

    const syncCropToRatio = useCallback((width, height) => {
      if (!imageSize.width || !imageSize.height) return;

      const targetRatio = width / height;
      const sourceRatio = imageSize.width / imageSize.height;

      let cropWidth, cropHeight, cropX, cropY;

      if (targetRatio >= sourceRatio) {
        cropWidth = imageSize.width;
        cropHeight = Math.round(imageSize.width / targetRatio);
        cropX = 0;
        cropY = Math.round((imageSize.height - cropHeight) / 2);
      } else {
        cropHeight = imageSize.height;
        cropWidth = Math.round(imageSize.height * targetRatio);
        cropY = 0;
        cropX = Math.round((imageSize.width - cropWidth) / 2);
      }

      studio.updateOperation('crop', {
        x: Math.max(0, cropX),
        y: Math.max(0, cropY),
        width: Math.min(imageSize.width, cropWidth),
        height: Math.min(imageSize.height, cropHeight),
        enabled: true,
      });
    }, [imageSize.width, imageSize.height, studio]);

    useEffect(() => {
      if (!isResizing) return undefined;

      const handleMouseMove = (e) => {
        if (!isResizing || !resizeHandle || !imageSize.width) return;

        const scaleFactorX = dragStart.width / (dragStart.displayWidth || 1);
        const scaleFactorY = dragStart.height / (dragStart.displayHeight || 1);

        const deltaX = (e.clientX - dragStart.x) * scaleFactorX;
        const deltaY = (e.clientY - dragStart.y) * scaleFactorY;

        let newWidth = dragStart.width;
        let newHeight = dragStart.height;

        const resizeOp = pipeline.find(op => op.type === 'resize');
        const ratio = imageSize.width / imageSize.height;

        if (resizeHandle.includes('right')) newWidth = Math.max(10, dragStart.width + deltaX);
        if (resizeHandle.includes('left')) newWidth = Math.max(10, dragStart.width - deltaX);
        if (resizeHandle.includes('bottom')) newHeight = Math.max(10, dragStart.height + deltaY);
        if (resizeHandle.includes('top')) newHeight = Math.max(10, dragStart.height - deltaY);

        if (resizeOp?.maintainAspectRatio) {
          if (resizeHandle.includes('right') || resizeHandle.includes('left')) {
            newHeight = Math.round(newWidth / ratio);
          } else {
            newWidth = Math.round(newHeight / ratio);
          }
        }

        studio.updateOperation('resize', {
          width: Math.round(newWidth),
          height: Math.round(newHeight),
          enabled: true,
        });

        if (activeOperation === 'crop') {
          syncCropToRatio(Math.round(newWidth), Math.round(newHeight));
        } else {
          const cropOp = pipeline.find(op => op.type === 'crop');
          if (cropOp?.enabled) {
            syncCropToRatio(Math.round(newWidth), Math.round(newHeight));
          }
        }
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        setResizeHandle(null);
      };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }, [isResizing, resizeHandle, dragStart, studio, imageSize, activeOperation, syncCropToRatio, pipeline]);




  const fastPreviewTimeoutRef = useRef(null);
  useEffect(() => {
    const effectOp = pipeline.find(op => op.type === "effect" && op.enabled);
    if (effectOp && effectOp.mood && effectOp.mood !== "none") {
      if (fastPreviewTimeoutRef.current) {
        clearTimeout(fastPreviewTimeoutRef.current);
      }
      fastPreviewTimeoutRef.current = setTimeout(async () => {
        try {
          if (!selectedFile) return;
          
          // This is a simplified approach. A more robust solution would handle
          // uploading the file if it hasn't been uploaded yet to get a fileId.
          const fileId = selectedFile.fileId || (await uploadFile(selectedFile)).fileId;
          if (!fileId) return;
          selectedFile.fileId = fileId; // Store for subsequent previews

          const response = await apiClient.post("/image/fast-preview", {
            fileId: fileId,
            fileName: selectedFile.name,
            operations: buildOperations(),
          });

          if (response.data.success) {
            const resultResponse = await apiClient.get(`/download/${response.data.data.filename}`, {
              responseType: "blob",
            });
            if (fastPreviewUrl) URL.revokeObjectURL(fastPreviewUrl);
            const nextPreviewUrl = window.URL.createObjectURL(new Blob([resultResponse.data]));
            setFastPreviewUrl(nextPreviewUrl);
          }
        } catch (error) {
          console.error("Fast preview failed:", error);
        }
      }, 300);
    } else {
        if (fastPreviewUrl) {
            URL.revokeObjectURL(fastPreviewUrl);
            setFastPreviewUrl("");
        }
    }
  }, [pipeline, selectedFile, buildOperations, fastPreviewUrl]);

  const handleAiAnalysis = async (file) => {
    if (!file) return;
    setIsAnalyzing(true);
    setAiMetadata(null);
    try {
      const uploadResponse = await uploadFile(file);
      if (uploadResponse?.success) {
        const response = await analyzeImageWithAI(uploadResponse.fileId, uploadResponse.fileName || file.name);
        if (response?.success) {
          setAiMetadata(response.data);
          showToast('AI analysis complete.', 'success');
        }
      }
    } catch (error) {
      console.error('AI Analysis failed:', error);
      showToast('AI analysis failed.', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleRender = async () => {
    if (isBatchMode) {
      await handleBatchRender();
      return;
    }

    if (!selectedFile || isProcessing) {
      if (!selectedFile) showToast('Load an image first.', 'error');
      return;
    }

    const currentToken = Date.now();
    uploadTokenRef.current = currentToken;
    setIsProcessing(true);

    try {
      const uploadResponse = await uploadFile(selectedFile);
      if (!uploadResponse?.success) {
        throw new Error(uploadResponse?.error || 'Upload failed');
      }

      const serverFileName = uploadResponse.serverFileName || `${uploadResponse.fileId}.png`;

      try {
        const metadataResponse = await getImageMetadata(serverFileName);
        if (metadataResponse?.success && metadataResponse?.data) {
          const { dimensions, fileSize, colorSpace, format } = metadataResponse.data;
          setMetadata({
            name: selectedFile.name,
            size: fileSize,
            type: format ? `image/${String(format).split(',')[0]}` : selectedFile.type,
            colorProfile: colorSpace || 'sRGB',
            lastModified: new Date(selectedFile.lastModified).toLocaleString(),
          });
          if (dimensions?.width && dimensions?.height) {
            setImageSize({ width: dimensions.width, height: dimensions.height });
          }
        }
      } catch (metadataError) {
        console.warn('Image metadata fetch failed, continuing with browser metadata.', metadataError);
      }

      const operations = buildOperations();
      const supported = await getSupportedImageFormats();
      const localSupportedOutputFormats = supported.output.map(f => f.value);
      if (!localSupportedOutputFormats.includes(outputFormat)) {
        throw new Error('Selected export format is not supported by the current encoder.');
      }

      const response = await processImagePipeline(
        uploadResponse.fileId,
        (aiMetadata && aiMetadata.suggestedFilename) ? `${aiMetadata.suggestedFilename}.${outputFormat}` : (uploadResponse.fileName || selectedFile.name),
        operations,
        outputFormat,
        quality,
        removeExif
      );

      if (uploadTokenRef.current !== currentToken) return;

      if (!response?.success) {
        throw new Error(response?.error || 'Image processing failed');
      }

      if (renderedPreviewUrl) {
        URL.revokeObjectURL(renderedPreviewUrl);
      }

      const resultResponse = await apiClient.get(`/download/${response.data.filename}`, {
        responseType: 'blob',
      });
      const nextPreviewUrl = window.URL.createObjectURL(new Blob([resultResponse.data]));

      setConversionResult(response.data);
      setRenderedPreviewUrl(nextPreviewUrl);
      showToast('Render finished successfully.', 'success');
    } catch (error) {
      console.error(error);
      showToast(error.message || 'Rendering failed.', 'error');
    } finally {
      if (uploadTokenRef.current === currentToken) {
        setIsProcessing(false);
      }
    }
  };

   const handleBatchRender = async () => {
     if (!studio.batchQueue.length || isBatchProcessing) {
       if (!studio.batchQueue.length) showToast('Add at least one image to the batch queue.', 'error');
       return;
     }

     const operations = buildOperations();

     const currentToken = Date.now();
     uploadTokenRef.current = currentToken;
     setIsBatchProcessing(true);
     setConversionResult(null);
     setBatchResult(null);
     setRenderedPreviewUrl('');
     studio.setBatchProgress({
       phase: 'uploading',
       progress: 0,
       label: operations.length > 0
         ? `Uploading ${studio.batchQueue.length} image${studio.batchQueue.length === 1 ? '' : 's'}...`
         : `Preparing ${studio.batchQueue.length} image${studio.batchQueue.length === 1 ? '' : 's'} for format conversion...`,
     });
     studio.batchQueue.forEach((file) => {
       const key = getBatchQueueKey(file);
       studio.setBatchItemState(key, {
         status: 'queued',
         progress: 0,
         label: 'Queued',
       });
     });

     try {
       const uploadedItems = [];
       const batchKeyByFileId = new Map();
       const totalFiles = studio.batchQueue.length;

       for (const [index, file] of studio.batchQueue.entries()) {
         const key = getBatchQueueKey(file);
         studio.setBatchItemState(key, {
           status: 'uploading',
           progress: 0,
           label: 'Uploading',
         });

         const uploadResponse = await uploadFile(file);
         if (!uploadResponse?.success) {
           throw new Error(uploadResponse?.error || `Upload failed for ${file.name}`);
         }

         studio.setBatchItemState(key, {
           status: 'uploaded',
           progress: 100,
           label: 'Uploaded',
         });

         uploadedItems.push({
           fileId: uploadResponse.fileId,
           fileName: uploadResponse.fileName || file.name,
         });
         batchKeyByFileId.set(uploadResponse.fileId, key);

         studio.setBatchProgress({
           phase: 'uploading',
           progress: Math.round(((index + 1) / totalFiles) * 45),
           label: `Uploaded ${index + 1} of ${totalFiles} image${totalFiles === 1 ? '' : 's'}.`,
         });
       }

       studio.setBatchProgress({
         phase: 'processing',
         progress: 55,
         label: 'Processing images on the server...',
       });

       studio.batchQueue.forEach((file) => {
         const key = getBatchQueueKey(file);
         studio.setBatchItemState(key, {
           status: 'processing',
           progress: 55,
           label: 'Processing',
         });
       });

       const batchRequest = buildBatchProcessRequest(uploadedItems, 'process', {
         operations,
         outputFormat,
         quality,
       });

       const response = await batchProcessImages(
         batchRequest.files,
         batchRequest.operation,
         batchRequest.settings,
       );

       if (uploadTokenRef.current !== currentToken) return;

       if (!response?.success && !response?.data) {
         throw new Error(response?.error || 'Batch processing failed');
       }

       const results = response?.data?.results || [];
       const successCount = response?.data?.summary?.succeeded ?? results.filter((item) => item.success).length;
       const failedCount = response?.data?.summary?.failed ?? results.length - successCount;

       setBatchResult(response.data);
       studio.setBatchProgress({
         phase: 'completed',
         progress: 100,
         label: `Batch finished: ${successCount} succeeded, ${failedCount} failed.`,
       });

       results.forEach((result) => {
         const matchedKey = batchKeyByFileId.get(result.fileId) || result.fileId;
         studio.setBatchItemState(matchedKey, {
           status: result.success ? 'done' : 'failed',
           progress: 100,
           label: result.success ? 'Completed' : 'Failed',
         });
       });

       const firstSuccessful = results.find((item) => item.success && item.filename);
       if (firstSuccessful) {
         const resultResponse = await apiClient.get(`/download/${firstSuccessful.filename}`, {
           responseType: 'blob',
         });
         const nextPreviewUrl = window.URL.createObjectURL(new Blob([resultResponse.data]));
         setRenderedPreviewUrl(nextPreviewUrl);
       }

       if (failedCount > 0) {
         showToast(`Batch finished with ${successCount} success${successCount === 1 ? '' : 'es'} and ${failedCount} failure${failedCount === 1 ? '' : 's'}.`, 'warning');
       } else {
         showToast(`Batch finished successfully for ${successCount} image${successCount === 1 ? '' : 's'}.`, 'success');
       }
     } catch (error) {
       console.error(error);
       const serverMessage = error?.response?.data?.error;
       showToast(serverMessage || error.message || 'Batch rendering failed.', 'error');
     } finally {
       if (uploadTokenRef.current === currentToken) {
         setIsBatchProcessing(false);
       }
     }
   };

  const handleDownload = async () => {
    const filename = isBatchMode
      ? batchResult?.archiveFilename
      : conversionResult?.filename;

    if (!filename) {
      showToast('Render the image first.', 'error');
      return;
    }

    try {
      if (includeAiMetadata && aiMetadata) {
        await downloadWithMetadata(filename, aiMetadata);
      } else {
        let displayFilename = filename;
        if (!isBatchMode && aiMetadata?.suggestedFilename) {
          const extension = outputFormat || 'jpg';
          displayFilename = `${aiMetadata.suggestedFilename}.${extension}`;
        }
        await downloadFile(filename, displayFilename);
      }
      showToast('Download started.', 'success');
    } catch (error) {
      console.error(error);
      showToast('Could not start download.', 'error');
    }
  };


  // LIVE PREVIEW: Computes CSS filters/transforms from current pipeline
  const previewImageStyle = useMemo(() => {
    if (!imageSize.width || !imageSize.height) return {};

    const effectOp = pipeline.find((op) => op.type === 'effect' && op.enabled);
    const rotateOp = pipeline.find((op) => op.type === 'rotate' && op.enabled);

    let filters = [];
    let transforms = [];

    // 1. ROTATION (preview)
    if (rotateOp?.angle) {
      transforms.push(`rotate(${rotateOp.angle}deg)`);
    }

    // 2. EFFECTS (brightness, contrast, saturation, mood)
    if (effectOp) {
      const { brightness = 0, contrast = 0, saturation = 1, mood = 'none', effect = 'none' } = effectOp;

      if (brightness !== 0) filters.push(`brightness(${1 + brightness})`);
      if (contrast !== 0) filters.push(`contrast(${1 + contrast})`);
      if (saturation !== 1) filters.push(`saturate(${saturation})`);

      // Mood presets
      if (mood && mood !== 'none') {
        switch (mood) {
          case 'cinematic':
            filters.push('contrast(1.2) saturate(0.85) hue-rotate(-5deg) sepia(0.1)');
            break;
          case 'vintage':
            filters.push('sepia(0.35) contrast(0.92) brightness(1.08)');
            break;
          case 'cyberpunk':
            filters.push('hue-rotate(190deg) saturate(1.8) contrast(1.12)');
            break;
          case 'golden':
            filters.push('sepia(0.25) saturate(1.35) brightness(1.04)');
            break;
          case 'dramatic':
            filters.push('contrast(1.35) brightness(0.96) saturate(1.15)');
            break;
          case 'noir':
            filters.push('grayscale(1) contrast(1.25) brightness(1.08)');
            break;
          default:
            break;
        }
      }

      // Special effects
      if (effect === 'blur') filters.push('blur(3px)');
      if (effect === 'grayscale') filters.push('grayscale(100%)');
      if (effect === 'sepia') filters.push('sepia(100%)');
      if (effect === 'sharpen') filters.push('contrast(1.15) brightness(1.05)');
    }

    // Resize preview handled by overlay, no CSS transform

    return {
      filter: filters.length > 0 ? filters.join(' ') : 'none',
      transform: transforms.join(' '),
      transition: 'filter 0.2s ease-out, transform 0.2s ease-out',
    };
  }, [pipeline, imageSize]);

  const canvasFrameStyle = useMemo(() => {
    if (!imageSize.width || !imageSize.height) return undefined;

    return {
      aspectRatio: `${imageSize.width} / ${imageSize.height}`,
      width: '100%',
      height: 'auto',
      maxWidth: '100%',
      maxHeight: '100%',
      display: 'flex',
    };
  }, [imageSize.width, imageSize.height]);

  // Resize overlay style for visual target area indicator and drag handles
  const resizeAspectRatio = useMemo(() => {
    if (!resizeOperation?.ratio) return null;
    if (resizeOperation.ratio === 'free') return null;
    if (resizeOperation.ratio === 'original') {
      if (imageSize.width && imageSize.height) {
        return imageSize.width / imageSize.height;
      }
      return null;
    }
    const parts = resizeOperation.ratio.split(':');
    if (parts.length === 2) {
      return parseFloat(parts[0]) / parseFloat(parts[1]);
    }
    return null;
  }, [resizeOperation?.ratio, imageSize.width, imageSize.height]);

  const handleResizeAspectRatioChange = useCallback((ratioValue) => {
    const match = [
      { key: 'free', value: null },
      { key: '1:1', value: 1 },
      { key: '4:3', value: 4 / 3 },
      { key: '16:9', value: 16 / 9 },
      { key: '9:16', value: 9 / 16 },
      { key: '3:2', value: 3 / 2 },
      { key: '21:9', value: 21 / 9 },
    ].find(r => r.value === ratioValue || (r.value && Math.abs(r.value - ratioValue) < 0.01));
    
    studio.updateOperation('resize', {
      ratio: match ? match.key : 'free',
      maintainAspectRatio: Boolean(match?.value),
    });
  }, [studio]);

  const handleResizeChange = useCallback((updates) => {
    const isFreeResize = updates.maintainAspectRatio === false || resizeOperation?.maintainAspectRatio === false || resizeOperation?.ratio === 'free';
    if (updates.width && updates.height) {
      if (isFreeResize) {
        updates.ratio = 'free';
        updates.maintainAspectRatio = false;
      } else {
        const currentRatio = updates.width / updates.height;
        const presets = [
          { key: '1:1', value: 1 },
          { key: '4:3', value: 4 / 3 },
          { key: '16:9', value: 16 / 9 },
          { key: '9:16', value: 9 / 16 },
          { key: '3:2', value: 3 / 2 },
          { key: '21:9', value: 21 / 9 },
        ];
        const originalRatio = imageSize.width && imageSize.height ? imageSize.width / imageSize.height : null;
        let match = presets.find(p => Math.abs(p.value - currentRatio) < 0.02);
        if (!match && originalRatio && Math.abs(originalRatio - currentRatio) < 0.02) {
          updates.ratio = 'original';
        } else {
          updates.ratio = match ? match.key : 'free';
        }
      }
    }
    studio.updateOperation('resize', updates);
  }, [studio, imageSize, resizeOperation]);

  const handleUpdateOperation = useCallback((type, updates) => {
    if (type === 'resize') {
      const isFreeResize = updates.maintainAspectRatio === false || resizeOperation?.maintainAspectRatio === false || resizeOperation?.ratio === 'free';
      const finalWidth = updates.width !== undefined ? updates.width : resizeOperation?.width;
      const finalHeight = updates.height !== undefined ? updates.height : resizeOperation?.height;
      if (finalWidth && finalHeight && updates.ratio === undefined) {
        if (isFreeResize) {
          updates.ratio = 'free';
        } else {
          const currentRatio = finalWidth / finalHeight;
          const presets = [
            { key: '1:1', value: 1 },
            { key: '4:3', value: 4 / 3 },
            { key: '16:9', value: 16 / 9 },
            { key: '9:16', value: 9 / 16 },
            { key: '3:2', value: 3 / 2 },
            { key: '21:9', value: 21 / 9 },
          ];
          const originalRatio = imageSize.width && imageSize.height ? imageSize.width / imageSize.height : null;
          let match = presets.find(p => Math.abs(p.value - currentRatio) < 0.02);
          if (!match && originalRatio && Math.abs(originalRatio - currentRatio) < 0.02) {
            updates.ratio = 'original';
          } else {
            updates.ratio = match ? match.key : 'free';
          }
        }
      }
    }
    studio.updateOperation(type, updates);
  }, [studio, resizeOperation, imageSize]);

  const resizeAspectLabel = resizeOperation?.ratio || 'Free';

  const outputInfo = conversionResult?.filename ? conversionResult.filename : 'No output has been generated yet.';
  const batchDownloadFilename = batchResult?.archiveFilename;
  const canDownloadResult = isBatchMode ? Boolean(batchDownloadFilename) : Boolean(conversionResult?.filename);
  const getBatchItemState = (file) => studio.batchItemStates[getBatchQueueKey(file)] || {
    status: 'queued',
    progress: 0,
    label: 'Queued',
  };
  const activeWatermark = pipeline.find((op) => op.type === 'watermark' && op.enabled);
  const watermarkPreview = activeWatermark ? (
    <div className={`watermark-preview position-${activeWatermark.position}`} style={{ opacity: activeWatermark.opacity / 100 }}>
      {activeWatermark.useImage && activeWatermark.imageFileId ? (
        <img
          src={`${API_BASE_URL.replace('/api', '')}/uploads/${activeWatermark.imageFileId}`}
          alt="Watermark Logo"
          className="wm-image-preview"
        />
      ) : (
        <span className="wm-text-preview">{activeWatermark.text || '© Your Brand'}</span>
      )}
    </div>
  ) : null;
  const handleSingleMode = () => setIsBatchMode(false);
  const handleBatchMode = () => {
    setIsBatchMode(true);
    prependSelectedFileToBatchQueue();
  };

   return (
     <div
       className={`image-studio-page ${studio.isDragOver ? 'drag-over' : ''}`}
       onDragEnter={(e) => {
         e.preventDefault();
         studio.setIsDragOver(true);
       }}
       onDragOver={(e) => e.preventDefault()}
       onDragLeave={() => studio.setIsDragOver(false)}
       onDrop={handleDrop}
     >
       {!selectedFile && (
         <>
           <video 
             autoPlay 
             muted 
             loop 
             playsInline 
             className="studio-bg-video"
           >
             <source src="/assets/videos/bg-video.mp4" type="video/mp4" />
           </video>
           <div className="studio-bg-overlay"></div>
         </>
       )}

       <StudioTopNav theme={theme} onToggleTheme={handleThemeToggle} />
       <main className="studio-grid">
         <ImagePipelinePanel
           selectedFile={selectedFile}
           enabledOperations={enabledOperations}
           pipeline={pipeline}
           activeOperation={activeOperation}
           setActiveOperation={setActiveOperation}
           dockTool={dockTool}
           setDockTool={setDockTool}
           expandOperation={studio.expandOperation}
           toggleOperation={studio.toggleOperation}
           updateOperation={handleUpdateOperation}
           imageSize={imageSize}
           OperationCard={OperationCard}
           theme={theme}
           onToggleTheme={handleThemeToggle}
         />

          <StudioCanvas
            previewDisplayUrl={previewUrl}
            selectedFileName={selectedFile?.name}
            renderedPreviewUrl={renderedPreviewUrl}
            canvasFrameStyle={canvasFrameStyle}
            previewImageStyle={previewImageStyle}
            resizeOverlayStyle={resizeOverlayStyle}
            isResizing={isResizing}
            resizeAspectLabel={resizeAspectLabel}
            
            targetWidthLabel={formatDimension(resizeOperation?.width || imageSize.width)}
            targetHeightLabel={formatDimension(resizeOperation?.height || imageSize.height)}
            originalAspectLabel={''}
            onResizeStart={handleResizeStart}
            watermarkPreview={watermarkPreview}
            onOpenFilePicker={() => fileInputRef.current.click()}
            isBatchMode={isBatchMode}
            isCropMode={studio.isCropMode}
            cropState={studio.cropState}
            onCropChange={studio.setCropState}
            onCropComplete={(c) => studio.updateOperation('crop', { ...c, enabled: true })}
            cropAspectRatio={cropAspectRatio}
            onCropAspectRatioChange={setCropAspectRatio}
            onExitCropMode={() => {
                  setDockTool('canvas');
              setActiveOperation('resize');
            }}
            imageSize={imageSize}
            cropOperation={pipeline.find(op => op.type === 'crop')}
            effectOperation={pipeline.find(op => op.type === 'effect')}
            zoom={zoom}
            onZoomChange={setZoom}
            pan={pan}
            onPanChange={setPan}
            activeOperation={activeOperation}
            resizeOperation={resizeOperation}
            onResizeChange={handleResizeChange}
            onResizeComplete={handleResizeChange}
            resizeAspectRatio={resizeAspectRatio}
            onResizeAspectRatioChange={handleResizeAspectRatioChange}
            onFocusNode={(type) => { studio.expandOperation(type); setActiveOperation(type); }}
            onUpdateOperation={handleUpdateOperation}
            pipeline={pipeline}
            onDownload={handleDownload}
           />

          <aside className="studio-column studio-right">
             <div className="inspector-stack">
               <ImageFileInfoCard
                 selectedFile={selectedFile}
                 isBatchMode={isBatchMode}
                 metadata={metadata}
                  aiMetadata={aiMetadata}
                  isAnalyzing={isAnalyzing}
                 imageSize={imageSize}
                 onSingleMode={handleSingleMode}
                 onBatchMode={handleBatchMode}
                 onAddImages={() => fileInputRef.current.click()}
                 formatBytes={formatBytes}
                 formatDimension={formatDimension}
               />

               {isBatchMode && (
                 <BatchQueueCard
                   batchQueue={studio.batchQueue}
                   batchProgress={studio.batchProgress}
                   batchItemStates={studio.batchItemStates}
                   isBatchProcessing={isBatchProcessing}
                   onAddImages={() => fileInputRef.current.click()}
                   onClearQueue={() => studio.setBatchQueue([])}
                   onRemoveFile={(file) => studio.setBatchQueue(studio.batchQueue.filter(f => f !== file))}
                   getBatchItemState={getBatchItemState}
                   getBatchQueueKey={getBatchQueueKey}
                  formatBytes={formatBytes}
                  truncateTwoWords={truncateTwoWords}
                />
              )}

               <ExportSettingsCard
                  enabledOperations={enabledOperations}
                  quality={quality}
                  onQualityChange={setQuality}
                  onResetToOriginal={() => studio.resetPipeline()}
                  imageReady={Boolean(imageSize.width)}
                  outputFormatOptions={[
                    { value: 'jpg', label: 'JPG' },
                    { value: 'png', label: 'PNG' },
                    { value: 'webp', label: 'WebP' },
                    { value: 'tiff', label: 'TIFF' },
                    { value: 'avif', label: 'AVIF' },
                    { value: 'bmp', label: 'BMP' },
                    { value: 'gif', label: 'GIF' },
                    { value: 'heic', label: 'HEIC' },
                    { value: 'ico', label: 'ICO' },
                  ]}
                  onClick={handleRender}
                  title="Done"
                  supportedOutputFormats={supportedOutputFormats}
                  outputFormat={outputFormat}
                  onOutputFormatChange={setOutputFormat}
                  isBatchMode={isBatchMode}
                 batchQueueLength={studio.batchQueue.length}
                 isBatchProcessing={isBatchProcessing}
                 selectedFileReady={Boolean(selectedFile)}
                 isProcessing={isProcessing}
                 onRender={handleRender}
                 removeExif={removeExif}
                 onRemoveExifChange={setRemoveExif}
               />

               <ImageResultCard
                 isBatchMode={isBatchMode}
                 batchResult={batchResult}
                 renderedPreviewUrl={renderedPreviewUrl}
                 outputInfo={outputInfo}
                 truncateTwoWords={truncateTwoWords}
                 canDownloadResult={canDownloadResult}
                 onDownload={handleDownload}
                 onToggleComparison={() => studio.setIsComparisonVisible(!studio.isComparisonVisible)}
                 includeAiMetadata={includeAiMetadata}
                 onToggleAiMetadata={() => setIncludeAiMetadata(!includeAiMetadata)}
                 aiMetadataAvailable={Boolean(aiMetadata)}
               />
            </div>
            </aside>
      </main>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={isBatchMode}
        className="hidden-file-input"
        onChange={handleInputChange}
      />

       {studio.isDragOver && <div className="drag-overlay">{isBatchMode ? 'Drop images to build the queue' : 'Drop image to import'}</div>}

      {studio.toast && (
        <Toast
          message={studio.toast.message}
          type={studio.toast.type}
          onClose={() => studio.setToast(null)}
        />
      )}

      {/* Before/After Comparison Slider */}
      <BeforeAfterSlider
        beforeImage={previewUrl}
        afterImage={renderedPreviewUrl}
        beforeLabel="Original"
        afterLabel="Edited"
        isVisible={studio.isComparisonVisible}
        onClose={() => studio.setIsComparisonVisible(false)}
      />
     </div>
   );
 };

 export default ImagePage;
